import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { defer, MonoTypeOperatorFunction, NEVER, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { isString, ShToastFactory } from '@stylehaven/components';

import { CustomErrorType } from '../../entities/common/custom-error-type.entity';
import {
    GenericActionFailureToast,
    GenericActionSuccessToast,
    GenericActionToast,
    GenericWarningToast
} from '../../helpers/generic-action-toast.helper';
import { ToastHelper } from '../../toast/service/toast.helper';
import { ToastUpdateOptions } from '../entity/toast-update-options.entity';

@Injectable()
export class OperatorFactory {
    readonly INDEX_TIMEOUT_ERRORS: CustomErrorType[] = [
        CustomErrorType.IndexTimeout,
        CustomErrorType.SaveIndexTimeout,
        CustomErrorType.StillInIndex
    ];

    constructor(
        private toastHelper: ToastHelper,
        private readonly translateService: TranslateService,
        private toastFactory: ShToastFactory
    ) {}

    /**
     * @deprecated Please use the method version that uses ToastUpdateOptions
     */
    withToastUpdates<T>(startedMsgKey: string, successMsgKey: string, errorMsgKey: string): MonoTypeOperatorFunction<T>;

    withToastUpdates<T>(options: ToastUpdateOptions): MonoTypeOperatorFunction<T>;

    /**
     * @deprecated Please use the method version that uses ToastUpdateOptions
     */
    withToastUpdates<T>(
        startedMsgKeyOrOptions: string | ToastUpdateOptions,
        successMsgKey?: string,
        errorMsgKey?: string
    ): MonoTypeOperatorFunction<T> {
        const options = this.getToastUpdateOptions(startedMsgKeyOrOptions, successMsgKey, errorMsgKey);

        return observable => {
            const toast = GenericActionToast(
                this.toastFactory,
                this.translateService.instant(options.startedMsgKey),
                options.link,
                options.linkText
            );
            return defer(() => {
                this.toastHelper.addToast(toast);
                return observable;
            }).pipe(
                catchError(error => {
                    if (this.isIndexTimeoutError(error)) {
                        this.toastHelper.updateToast(
                            toast.id,
                            GenericWarningToast(
                                this.translateService.instant('ui.errors.index-timeout'),
                                options.link,
                                options.linkText
                            )
                        );
                    } else {
                        this.toastHelper.updateToast(
                            toast.id,
                            GenericActionFailureToast(
                                this.translateService.instant(options.errorMsgKey!),
                                options.link,
                                options.linkText
                            )
                        );
                    }
                    return throwError(error);
                }),
                tap(() => {
                    if (options.removeOnSuccess === true) {
                        this.toastHelper.remove(toast.id);
                    } else {
                        const successToast = GenericActionSuccessToast(
                            this.translateService.instant(options.successMsgKey!),
                            options.link,
                            options.linkText
                        );
                        this.toastHelper.updateToast(toast.id, successToast);
                    }
                })
            );
        };
    }

    private getToastUpdateOptions(
        startedMsgKeyOrOptions: string | ToastUpdateOptions,
        successMsgKey?: string,
        errorMsgKey?: string
    ): ToastUpdateOptions {
        let options: ToastUpdateOptions;
        if (isString(startedMsgKeyOrOptions)) {
            options = {
                startedMsgKey: startedMsgKeyOrOptions,
                successMsgKey,
                errorMsgKey
            };
        } else {
            options = startedMsgKeyOrOptions as ToastUpdateOptions;
        }

        if (!options.successMsgKey) {
            options = {
                ...options,
                successMsgKey: `${options.startedMsgKey}.completed`,
                errorMsgKey: `${options.startedMsgKey}.failed`,
                startedMsgKey: `${options.startedMsgKey}.started`
            };
        }

        return options;
    }

    private isIndexTimeoutError(error: any) {
        return this.INDEX_TIMEOUT_ERRORS.includes(error);
    }

    withLoadingHandler<T>(
        loader$: Subject<boolean>,
        error$: Subject<boolean> | null = null,
        errorResponse: Observable<any> = NEVER,
        softErrorResponse: Observable<any> = of(true)
    ): MonoTypeOperatorFunction<T> {
        return observable =>
            defer(() => {
                loader$.next(true);
                if (error$) {
                    error$.next(false);
                }
                return observable;
            }).pipe(
                catchError(error => {
                    if (this.isIndexTimeoutError(error)) {
                        return softErrorResponse;
                    } else {
                        if (error$) {
                            error$.next(true);
                        }
                        console.error(error);
                        return errorResponse;
                    }
                }),
                finalize(() => loader$.next(false))
            );
    }
}
